import { useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';

import { ExpandMoreIcon } from '@occc-app/design-system';
import { RetailerNetworkLocationGroupFragment } from '@occc/graphql';

import { LocationGroupHeader } from './location-group-header.component';
import { LocationGroupStoresGrid } from './location-group-stores-grid.component';
import { OTHERS_GROUP_ID, useLocationGroupStores } from './use-location-group-stores.hook';
import { useLocationGroupOnlineStatus } from './use-location-group-online-status.hook';

import { ConnectionStatus } from '../retailer-network-my-stores-v2-page.types';

export { OTHERS_GROUP_ID };

type OthersGroup = { uuid: typeof OTHERS_GROUP_ID; name: 'Others' };
export type GroupProp = RetailerNetworkLocationGroupFragment | OthersGroup;

type LocationGroupSectionProps = {
  group: GroupProp;
  networkId: string;
  allGroupUuids?: string[];
  stateFilter?: string;
  cityFilter?: string;
  connectionStatusFilter?: ConnectionStatus | '';
  defaultExpanded?: boolean;
  hideWhenEmpty?: boolean;
};

export const LocationGroupSection = ({
  group,
  networkId,
  allGroupUuids,
  stateFilter,
  cityFilter,
  connectionStatusFilter,
  defaultExpanded = true,
  hideWhenEmpty = false,
}: LocationGroupSectionProps) => {
  const isOthers = group.uuid === OTHERS_GROUP_ID;

  const [expanded, setExpanded] = useState(defaultExpanded);
  // Latch to true once expanded so the query is never re-skipped after first load.
  const [hasExpanded, setHasExpanded] = useState(defaultExpanded);

  const handleChange = (_: unknown, isExpanded: boolean) => {
    setExpanded(isExpanded);
    if (isExpanded) setHasExpanded(true);
  };

  const {
    displayedLocations,
    isInitialLoading,
    loading,
    paginationProps,
    isRemoveInProgress,
    handleRemoveFromLocationGroup,
  } = useLocationGroupStores({
    groupUuid: group.uuid,
    networkId,
    allGroupUuids,
    stateFilter,
    cityFilter,
    connectionStatusFilter,
    skip: !hasExpanded,
  });

  const shouldHide =
    hideWhenEmpty && !isInitialLoading && !loading && displayedLocations.length === 0;

  // "Others" has no real group UUID to filter by, so it's excluded from the online-status badge.
  const { offlineCount } = useLocationGroupOnlineStatus({
    groupUuid: group.uuid,
    networkId,
    skip: isOthers || shouldHide,
  });

  if (shouldHide) {
    return null;
  }

  // countOfLocations is authoritative for named groups; "Others" has no server-side total,
  // so we pass null and the header omits the count rather than showing a misleading page-only value.
  const storeCount = 'countOfLocations' in group ? group.countOfLocations : null;

  return (
    <Accordion
      expanded={expanded}
      onChange={handleChange}
      disableGutters
      elevation={1}
      sx={{
        borderRadius: 2,
        '&.MuiAccordion-root:before': { display: 'none' },
        '&.MuiAccordion-root': { borderRadius: 2 },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 3,
          minHeight: 64,
          '&.Mui-expanded': { borderBottom: '1px solid', borderColor: 'divider' },
          '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1, my: 0 },
          '& .MuiAccordionSummary-expandIconWrapper': { order: -1, mr: 1, ml: 0 },
        }}
      >
        <LocationGroupHeader
          groupName={group.name}
          groupUuid={group.uuid}
          isOthers={isOthers}
          storeCount={storeCount}
          offlineCount={offlineCount}
          networkId={networkId}
        />
      </AccordionSummary>

      <AccordionDetails sx={{ px: 3, pt: 3, pb: 1 }}>
        <LocationGroupStoresGrid
          displayedLocations={displayedLocations}
          loading={loading}
          isInitialLoading={isInitialLoading}
          networkId={networkId}
          locationGroupId={isOthers ? undefined : group.uuid}
          locationGroupName={isOthers ? undefined : group.name}
          onRemoveFromLocationGroup={isOthers ? undefined : handleRemoveFromLocationGroup}
          isRemoveInProgress={isRemoveInProgress}
          paginationProps={paginationProps}
        />
      </AccordionDetails>
    </Accordion>
  );
};
